<template>
  <!-- eslint-disable  max-len -->
  <div
    id="okta-authenticator"
    class="configuration"
  >
    <template v-if="isActivateMode">
      <div class="form-container">
        <div class="info-icon-container">
          <label class="form-label">Authenticator Name</label>
          <span
            v-tooltip="{
              content: 'Choose a descriptive name for this authenticator.',
              trigger: 'mouseenter click focus',
            }"
            class="info-icon"
            v-html="infoIcon"
          />
        </div>
        <input
          id="name-input-okta"
          v-model="authenticatorName"
          class="form-field"
          type="text"
          maxlength="50"
        />
      </div>
      <template v-for="(field, index) in fields">
        <div
          :key="index"
          class="form-container"
        >
          <div class="form-label-container">
            <div class="info-icon-container">
              <label class="form-label">{{ field.label }}</label>
              <template v-if="field.infoMessage">
                <span
                  v-tooltip="{ content: field.infoMessage, trigger: 'mouseenter click focus' }"
                  class="info-icon"
                  v-html="infoIcon"
                />
              </template>
            </div>
            <div class="form__error">
              {{ inputErrors[field.id] }}
            </div>
          </div>
          <div class="form-field-inline">
            <input
              :id="field.id + '-input-okta'"
              v-model="field.input"
              class="form-field"
              type="text"
            />
          </div>
          <template v-if="field.id === 'adminUrl' && field.input">
            <div class="form-field__description">
              https://{{ field.input }}.okta.com
            </div>
          </template>
        </div>
      </template>
      <div class="mb-1">
        The default authenticator will handle 2FA account-wide.
      </div>
      <label
        class="checkbox mb-1 ml-1"
        for="default-authenticator-check"
      >
        <input
          id="default-authenticator-check"
          v-model="defaultAuthenticatorCheck"
          type="checkbox"
        />
        <span>Set as Default Authenticator</span>
      </label>
      <div class="configuration-footer">
        <a
          :href="oktaUrl"
          target="_blank"
        > Where do I find my Okta API Token? </a>
        <div
          id="okta-submit-error"
          class="configuration-footer__error"
        >
          {{ errorMessage }}
        </div>
        <div class="configuration-footer-actions">
          <button
            id="cancel-edit-button-okta"
            class="btn btn--grey mr-2"
            @click="emitCancel"
          >
            Cancel
          </button>
          <button
            id="submit-edit-button-okta"
            class="btn"
            @click="emitCreateCredential"
          >
            Activate OKTA
          </button>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="flex-grid">
        <div class="column--two">
          <div class="configuration-content__heading">
            Okta is available to handle second-factor authentication.
          </div>
          <div class="configuration-content__description">
            To check if Okta is working correctly, go to the User Management page, and select ‘Send
            Test Request’ on a user to send them an authentication request.
            <strong>Usernames must match within Okta.</strong>
          </div>
          <button
            id="okta-deactivate-button"
            class="btn btn__text btn__text--red mt-2"
            @click="deleteConfiguration"
          >
            Delete Authenticator
          </button>
        </div>
        <div class="column--two">
          <div class="mb-1">
            The default authenticator will handle 2FA account-wide.
          </div>
          <label
            class="checkbox mb-1 ml-1 mt-2"
            for="default-authenticator-check"
          >
            <input
              id="default-authenticator-check"
              v-model="defaultAuthenticatorCheck"
              type="checkbox"
            />
            <span>Set as Default Authenticator</span>
          </label>
          <div class="mt-2">
            <button
              id="cancel-edit-button-okta"
              class="btn btn--grey mr-2"
              @click="emitCancel"
            >
              Cancel
            </button>
            <button
              id="submit-edit-button-okta"
              class="btn"
              :disabled="!isDefault && !defaultAuthenticatorCheck"
              @click="emitUpdate"
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
  <!-- eslint-enable  max-len -->
</template>

<script>
import feather from 'feather-icons'
import AuthenticatorMixin from '@/mixins/authenticatorMixin'

export default {
  name: 'OktaConfiguration',
  mixins: [AuthenticatorMixin],
  props: [
    'selectedAuthenticator',
    'defaultAuthenticator',
    'isActivateMode',
    'parentError',
    'loading',
  ],
  data() {
    return {
      errorMessage: '',
      authenticatorName: '',
      defaultAuthenticatorCheck: false,
      oktaUrl: 'https://support.okta.com/help/s/article/How-do-I-create-an-API-token',
      fields: [
        {
          id: 'adminUrl',
          label: 'Okta Domain',
          input: '',
          required: true,
          infoMessage: 'https://{yourOktaDomain}.okta.com',
        },
        {
          id: 'username',
          label: 'Okta Username',
          input: '',
          required: true,
        },
        {
          id: 'token',
          label: 'Okta API Token',
          input: '',
          required: true,
        },
      ],
      infoIcon: feather.icons.info.toSvg({width: '1em', height: '1em'}),
    }
  },
  computed: {
    isDefault() {
      return this.isDefaultAuthenticator(this.defaultAuthenticator, this.AUTH_TYPES.OKTA)
    },
    configuration() {
      const _configuration = {}
      this.fields.forEach((field) => {
        if (field.id === 'adminUrl') {
          _configuration[field.id] = `${field.input}.okta.com`
        } else {
          _configuration[field.id] = field.input
        }
      })
      return _configuration
    },
    inputErrors() {
      const _errors = this.fields.reduce((errors, field) => {
        const {input, id} = field
        if (!input) {
          errors[id] = 'Required'
        }
        return errors
      }, {})
      return _errors
    },
  },
  created() {
    this.defaultAuthenticatorCheck = this.isDefault
  },
  methods: {
    /*
     * Check if user is setting the authenticator to be the default
     * or if the authenticator is already the default
     * authenticator, then set to none. If update button will be disabled if
     * this is not the default authenticator or the checkbox is not checked.
     */
    emitUpdate() {
      const _accountSettingPayload = this.generateSettingsPayload(
        this.defaultAuthenticatorCheck,
        this.AUTH_TYPES.OKTA,
        this.selectedAuthenticator.uuid,
        this.defaultAuthenticator.bcidSettings,
      )
      this.$emit('update-setting', _accountSettingPayload)
    },
    emitCreateCredential() {
      this.errorMessage = ''
      if (Object.entries(this.inputErrors).length === 0) {
        const _credentialPayload = {
          setDefault: this.defaultAuthenticatorCheck,
          credential: {
            name: this.authenticatorName || 'Okta Authenticator',
            description: 'This is the okta credential.',
            url: 'www.okta.com/',
            type: this.AUTH_TYPES.OKTA,
            rawData: this.configuration,
          },
        }
        this.$emit('create-credential', _credentialPayload)
      } else {
        this.errorMessage = 'Please make sure all fields are filled in correctly.'
      }
    },
    deleteConfiguration() {
      this.$emit('deactivate')
    },
    emitCancel() {
      this.$emit('cancel')
    },
  },
}
</script>

<style lang="scss" scoped>
.configuration {
  width: 100%;
  &-content {
    @include flex-display();
    @include flex-direction(column);
    width: 50%;
    &__heading {
      font-weight: 600;
      margin-bottom: 1em;
    }
    &__description {
      color: $grey-text;
      line-height: 1.5em;
    }
  }
  &-footer {
    width: 100%;
    @include flex-display();
    @include flex-justify-content(space-between);
    @include flex-align-items(center);
    &__error {
      color: $red;
    }
  }
}
</style>
